import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedIcon from '@mui/icons-material/Verified';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

const stepIcons = {
  4: CheckCircleIcon,
  3: DescriptionIcon,
  2: VerifiedIcon,
  1: TrendingUpIcon,
};

const StepCard = ({ step, isLast }) => {
  const [expanded, setExpanded] = useState(step.stepNumber === 1);
  const Icon = stepIcons[step.stepNumber] || CircleIcon;

  return (
    <Box sx={{ display: 'flex', gap: 2, position: 'relative' }}>
      {/* Timeline line */}
      {!isLast && (
        <Box
          sx={{
            position: 'absolute',
            left: 15,
            top: 36,
            bottom: -8,
            width: 2,
            bgcolor: '#E5E7EB',
          }}
        />
      )}

      {/* Step icon */}
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: `${step.statusColor}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          mt: 0.5,
          zIndex: 1,
        }}
      >
        <Icon sx={{ fontSize: 18, color: step.statusColor }} />
      </Box>

      {/* Step content */}
      <Box sx={{ flex: 1, pb: isLast ? 0 : 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
            Step {step.stepNumber} : {step.title}
          </Typography>
        </Box>

        <Box
          onClick={() => setExpanded(!expanded)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            py: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: '#1B2A3D' }}>
              {step.subtitle}
            </Typography>
            <Chip
              label={step.status}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.6875rem',
                fontWeight: 600,
                bgcolor: `${step.statusColor}15`,
                color: step.statusColor,
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small">
              <ExpandMoreIcon
                sx={{
                  fontSize: 20,
                  transform: expanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          </Box>
        </Box>

        <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', mb: 0.5 }}>
          {step.date}
        </Typography>

        <Collapse in={expanded}>
          <Typography sx={{ fontSize: '0.8125rem', color: '#4B5563', lineHeight: 1.7, mt: 1 }}>
            {step.description}
          </Typography>

          {step.detailedAnalysis && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1B2A3D', mb: 0.5 }}>
                Detailed Analysis
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: '#4B5563', lineHeight: 1.7 }}>
                {step.detailedAnalysis}
              </Typography>
            </Box>
          )}

          {step.agentInfo && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1B2A3D', mb: 1.5 }}>
                Agent & Workflow Information
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {[
                  { label: 'Agent Name', value: step.agentInfo.agentName, icon: '🤖' },
                  { label: 'Version', value: step.agentInfo.version, icon: '📋' },
                  { label: 'Workflow', value: step.agentInfo.workflow, icon: '⚡' },
                ].map((item) => (
                  <Box key={item.label}>
                    <Typography sx={{ fontSize: '0.6875rem', color: '#9CA3AF' }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8125rem', color: '#1B2A3D', fontWeight: 500 }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 3, mt: 1.5 }}>
                {[
                  { label: 'Risk Score', value: step.agentInfo.riskScore },
                  { label: 'Confidence', value: `${step.agentInfo.confidence}%` },
                  { label: 'Next Agent', value: step.agentInfo.nextAgent },
                ].map((item) => (
                  <Box key={item.label}>
                    <Typography sx={{ fontSize: '0.6875rem', color: '#9CA3AF' }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.8125rem', color: '#1B2A3D', fontWeight: 500 }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Collapse>
      </Box>
    </Box>
  );
};

const LogsAndEvents = ({ data }) => {
  return (
    <Card sx={{ borderRadius: 1, mb: 3 }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
          <HistoryIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary' }}>
            Logs and Events
          </Typography>
        </Box>

        {/* Automated Workflow Tag */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: '#1B2A3D', mb: 0.5 }}>
            Automated Workflow{' '}
            <Chip
              label={data.workflow.label}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.6875rem',
                fontWeight: 600,
                bgcolor: '#EDE9FE',
                color: '#7C3AED',
                ml: 1,
              }}
            />
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
            {data.workflow.flowId}
          </Typography>
        </Box>

        {/* Decision Reasoning */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1B2A3D', mb: 1 }}>
            {data.reasoning.title}
          </Typography>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1B2A3D', mb: 0.75 }}>
            Data Inputs Analyzed
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1.5 }}>
            {data.reasoning.dataInputs.map((input, idx) => (
              <Box
                component="li"
                key={idx}
                sx={{ fontSize: '0.8125rem', color: '#4B5563', lineHeight: 1.8 }}
              >
                {input}
              </Box>
            ))}
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1B2A3D', mb: 0.5 }}>
            Analysis & Findings
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: '#4B5563', lineHeight: 1.7 }}>
            {data.reasoning.analysisFindings}
          </Typography>
        </Box>

        {/* Prediction Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: '#1B2A3D',
            color: '#fff',
            borderRadius: 2,
            px: 2,
            py: 1.25,
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToyIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
              {data.prediction.label}
            </Typography>
          </Box>
          <Chip
            label={`${data.prediction.confidence}% confidence`}
            size="small"
            sx={{
              height: 24,
              fontSize: '0.6875rem',
              fontWeight: 600,
              bgcolor: 'rgba(255,255,255,0.15)',
              color: '#fff',
            }}
          />
        </Box>

        {/* Recommendation Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: '#399432',
            color: '#fff',
            borderRadius: 2,
            px: 2,
            py: 1.25,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountTreeIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
              {data.recommendation.label}
            </Typography>
          </Box>
          {/* <Chip
            label={`${data.recommendation.confidence}% confidence`}
            size="small"
            sx={{
              height: 24,
              fontSize: '0.6875rem',
              fontWeight: 600,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: '#fff',
            }}
          /> */}
        </Box>

        {/* Timeline Steps */}
        <Box>
          {data.steps.map((step, index) => (
            <StepCard
              key={step.stepNumber}
              step={step}
              isLast={index === data.steps.length - 1}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default LogsAndEvents;
